import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.unrealmi.site.components.layouts.MarkdownLayout as _com_unrealmi_site_components_layouts_MarkdownLayout
import com.unrealmi.site.components.layouts.PageLayout as _com_unrealmi_site_components_layouts_PageLayout
import com.unrealmi.site.pages.AboutPage as _com_unrealmi_site_pages_AboutPage
import com.unrealmi.site.pages.HomePage as _com_unrealmi_site_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "UnrealMiSite"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.unrealmi.site.components.layouts.MarkdownLayout",
            parentLayoutId = "com.unrealmi.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.unrealmi.site.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_unrealmi_site_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.unrealmi.site.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_unrealmi_site_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.unrealmi.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.unrealmi.site.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_unrealmi_site_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "com.unrealmi.site.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.unrealmi.site.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _com_unrealmi_site_pages_AboutPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.unrealmi.site.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.unrealmi.site.SubheadlineTextStyle)
        ctx.theme.registerStyle("eyebrow", com.unrealmi.site.EyebrowStyle)
        ctx.theme.registerStyle("section-title", com.unrealmi.site.SectionTitleStyle)
        ctx.theme.registerStyle("markdown", com.unrealmi.site.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("footer", com.unrealmi.site.components.sections.FooterStyle)
        ctx.theme.registerStyle("footer-inner", com.unrealmi.site.components.sections.FooterInnerStyle)
        ctx.theme.registerStyle("footer-link", com.unrealmi.site.components.sections.FooterLinkStyle)
        ctx.theme.registerStyle("nav-header", com.unrealmi.site.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("nav-link", com.unrealmi.site.components.sections.NavLinkStyle)
        ctx.theme.registerStyle("section", com.unrealmi.site.pages.SectionStyle)
        ctx.theme.registerStyle("glass-card", com.unrealmi.site.pages.GlassCardStyle)
        ctx.theme.registerStyle("hero-section", com.unrealmi.site.pages.HeroSectionStyle)
        ctx.theme.registerStyle("stats-grid", com.unrealmi.site.pages.StatsGridStyle)
        ctx.theme.registerStyle("capability-grid", com.unrealmi.site.pages.CapabilityGridStyle)
        ctx.theme.registerStyle("vision-grid", com.unrealmi.site.pages.VisionGridStyle)
        ctx.theme.registerVariant("-circle", com.unrealmi.site.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", com.unrealmi.site.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("spin", com.unrealmi.site.SpinAnim)
        ctx.theme.registerKeyframes("float", com.unrealmi.site.FloatAnim)
        ctx.theme.registerKeyframes("rise", com.unrealmi.site.RiseAnim)
        ctx.theme.registerKeyframes("aurora", com.unrealmi.site.AuroraAnim)
        ctx.theme.registerKeyframes("pulse", com.unrealmi.site.PulseAnim)
        ctx.theme.registerKeyframes("side-menu-slide-in", com.unrealmi.site.components.sections.SideMenuSlideInAnim)
        com.unrealmi.site.initColorMode(ctx)
        com.unrealmi.site.initStyles(ctx)
        com.unrealmi.site.initSiteStyles(ctx)
        com.unrealmi.site.initTheme(ctx)
    }

    router.tryRoutingTo(BasePath.remove(window.location.href.removePrefix(window.origin)), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.unrealmi.site.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
